import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Eye, Image as ImageIcon, Calendar, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import type { Tables } from "@/integrations/supabase/types";
import { differenceInDays, format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

type Document = Tables<"documents">;

interface SourceDocumentCardProps {
  demarcheId: string;
}

export function SourceDocumentCard({ demarcheId }: SourceDocumentCardProps) {
  const [sourceDoc, setSourceDoc] = useState<Document | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { openDocument } = useDocumentPreview();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSourceDocument = async () => {
      try {
        // 1. First check source_document_id in demarches table
        const { data: demarche, error: demarcheError } = await supabase
          .from('demarches')
          .select('source_document_id')
          .eq('id', demarcheId)
          .single();

        if (demarcheError) throw demarcheError;

        let docId = demarche?.source_document_id;

        // 2. Fallback: get first linked document from demarche_documents
        if (!docId) {
          const { data: linkedDoc } = await supabase
            .from('demarche_documents')
            .select('document_id')
            .eq('demarche_id', demarcheId)
            .not('document_id', 'is', null)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          docId = linkedDoc?.document_id;
        }

        // 3. Fetch the full document if we have an ID
        if (docId) {
          const { data: doc, error: docError } = await supabase
            .from('documents')
            .select('*')
            .eq('id', docId)
            .single();

          if (docError) throw docError;

          if (doc) {
            setSourceDoc(doc);

            // Get thumbnail URL
            if (doc.thumbnail_path) {
              const { data: urlData } = await supabase.storage
                .from('thumbnails')
                .createSignedUrl(doc.thumbnail_path, 3600);
              if (urlData?.signedUrl) {
                setThumbnailUrl(urlData.signedUrl);
              }
            } else if (doc.file_path) {
              // Fallback: use the document itself as thumbnail for images
              const isImage = doc.mime_type?.startsWith('image/');
              if (isImage) {
                const { data: urlData } = await supabase.storage
                  .from('documents')
                  .createSignedUrl(doc.file_path, 3600);
                if (urlData?.signedUrl) {
                  setThumbnailUrl(urlData.signedUrl);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching source document:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSourceDocument();
  }, [demarcheId]);

  const handleViewDocument = () => {
    if (sourceDoc) {
      openDocument(sourceDoc);
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Skeleton className="w-16 h-20 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-9 w-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!sourceDoc) {
    return null;
  }

  const isImage = sourceDoc.mime_type?.startsWith('image/');
  const isPdf = sourceDoc.mime_type === 'application/pdf';

  return (
    <Card className="mb-6 border-primary/20 bg-primary/5">
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">
          Document à l'origine
        </p>
        <div className="flex items-center gap-4">
          {/* Thumbnail */}
          <div className="w-16 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0 border">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={sourceDoc.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {isPdf ? (
                  <FileText className="w-8 h-8 text-muted-foreground" />
                ) : isImage ? (
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                ) : (
                  <FileText className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm truncate">{sourceDoc.title}</h3>
            <p className="text-xs text-muted-foreground truncate">
              {sourceDoc.file_name}
            </p>
            {/* Expiration date */}
            {sourceDoc.expires_at && (
              <div className="mt-1">
                {(() => {
                  const daysUntil = differenceInDays(new Date(sourceDoc.expires_at), new Date());
                  const isExpired = daysUntil < 0;
                  const isUrgent = daysUntil >= 0 && daysUntil <= 30;
                  
                  return (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] gap-1 cursor-pointer hover:opacity-80 transition-opacity",
                        isExpired && "bg-destructive/10 text-destructive border-destructive/30",
                        isUrgent && !isExpired && "bg-warning/10 text-warning border-warning/30",
                        !isExpired && !isUrgent && "bg-muted text-muted-foreground"
                      )}
                      onClick={() => navigate(`/documents/${sourceDoc.id}`)}
                    >
                      {isExpired ? (
                        <AlertTriangle className="w-3 h-3" />
                      ) : (
                        <Calendar className="w-3 h-3" />
                      )}
                      {isExpired 
                        ? `Expiré depuis ${Math.abs(daysUntil)}j`
                        : `Expire le ${format(new Date(sourceDoc.expires_at), "d MMM yyyy", { locale: fr })}`
                      }
                    </Badge>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Action */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDocument}
            className="flex-shrink-0 gap-2"
          >
            <Eye className="w-4 h-4" />
            Consulter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
